import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiClient from 'services/_infra/apiClient';
import {
  mergeSupplementalNotes,
  normalizeRunEvents,
} from 'features/Responder/features/Schedules/features/RunEventHistory/eventHistoryUtils';

const eventTime = (event) => {
  if (!event?.timestamp) return 0;
  const parsed = new Date(event.timestamp).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortByNewest = (events = []) => events.slice().sort((a, b) => eventTime(b) - eventTime(a));

const didEventChange = (previous, next) => {
  if (!previous || !next) return true;
  return (
    previous.id !== next.id ||
    previous.kind !== next.kind ||
    previous.label !== next.label ||
    previous.timestamp !== next.timestamp ||
    previous.note !== next.note ||
    previous.details !== next.details ||
    previous.latLng?.lat !== next.latLng?.lat ||
    previous.latLng?.lng !== next.latLng?.lng
  );
};

const isSameEventList = (previous = [], next = []) => {
  if (previous.length !== next.length) return false;
  for (let i = 0; i < previous.length; i += 1) {
    if (didEventChange(previous[i], next[i])) return false;
  }
  return true;
};

const mergeRunEvents = (previous = [], incoming = []) => {
  const previousMap = new Map(previous.map((event) => [`${event.id}`, event]));
  const incomingMap = new Map(incoming.map((event) => [`${event.id}`, event]));

  const merged = [];
  incomingMap.forEach((event, id) => {
    const previousEvent = previousMap.get(id);
    merged.push(previousEvent && !didEventChange(previousEvent, event) ? previousEvent : event);
  });

  previousMap.forEach((event, id) => {
    if (!incomingMap.has(id)) merged.push(event);
  });

  return sortByNewest(merged);
};

export const useRunEventHistory = ({
  runId,
  supplementalNotes = [],
  liveUpdatesEnabled = true,
  pollIntervalMs = 3000,
} = {}) => {
  const [baseEvents, setBaseEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eventsLastUpdated, setEventsLastUpdated] = useState(null);
  const inFlightRef = useRef(false);

  const normalizedRunId = useMemo(() => {
    if (runId == null) return null;
    const numericId = Number(runId);
    if (!Number.isFinite(numericId)) return null;
    return `${numericId}`;
  }, [runId]);

  const notes = useMemo(
    () => (Array.isArray(supplementalNotes) ? supplementalNotes : []),
    [supplementalNotes],
  );

  const fetchEvents = useCallback(
    async ({ silent = false } = {}) => {
      if (!normalizedRunId) {
        setBaseEvents([]);
        setEventsLastUpdated(null);
        setError(null);
        setLoading(false);
        return;
      }
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const requestTs = Date.now();
        const res = await apiClient.get(`/responder/event-history/run/${normalizedRunId}`, {
          params: { _ts: requestTs },
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        });
        const results = res?.data?.results ?? res?.data ?? [];
        const rawEvents = Array.isArray(results) ? results : [];
        const normalized = normalizeRunEvents(rawEvents);

        setBaseEvents((previous) => {
          const merged = mergeRunEvents(previous, normalized);
          return isSameEventList(previous, merged) ? previous : merged;
        });
        setEventsLastUpdated(new Date());
        setError(null);
      } catch (err) {
        if (!silent) {
          setError(err);
          setBaseEvents([]);
        }
      } finally {
        if (!silent) setLoading(false);
        inFlightRef.current = false;
      }
    },
    [normalizedRunId],
  );

  useEffect(() => {
    inFlightRef.current = false;
    setError(null);
    if (!normalizedRunId) {
      setBaseEvents([]);
      setEventsLastUpdated(null);
      setLoading(false);
      return;
    }
    setBaseEvents([]);
    fetchEvents();
  }, [fetchEvents, normalizedRunId]);

  useEffect(() => {
    if (!normalizedRunId || !liveUpdatesEnabled) return undefined;
    if (!Number.isFinite(pollIntervalMs) || pollIntervalMs <= 0) return undefined;
    const intervalId = setInterval(() => {
      fetchEvents({ silent: true });
    }, pollIntervalMs);
    return () => clearInterval(intervalId);
  }, [fetchEvents, liveUpdatesEnabled, normalizedRunId, pollIntervalMs]);

  const events = useMemo(
    () => mergeSupplementalNotes(baseEvents, notes, normalizedRunId),
    [baseEvents, notes, normalizedRunId],
  );

  return {
    events,
    loading,
    error,
    refresh: fetchEvents,
    lastUpdated: eventsLastUpdated,
  };
};
